import React from "react";
import LegalPageLayout from "./LegalPageLayout";

const PrivacyPolicy = () => {
  document.title = "AMANAT - Privacy Policy";

  return (
    <LegalPageLayout>
      <div className="legal-doc-header">
        <p className="legal-doc-brand">AMANAT</p>
        <p className="legal-doc-company">
          Colca Services W.L.L — CR No. 178727-1 — Kingdom of Bahrain
        </p>
        <h1 className="legal-doc-title">PRIVACY POLICY</h1>
        <p className="legal-doc-version">Version 2</p>
        <p className="legal-doc-dates">
          Effective Date: 1 July 2025 | Last Revised: 1 August 2026
        </p>
      </div>

      <h2>1. Introduction and Scope</h2>
      <h3>1.1</h3>
      <p>
        Colca Services W.L.L, trading as Amanat (&apos;Amanat&apos;, &apos;we&apos;,
        &apos;our&apos;, &apos;us&apos;), is a company duly registered under CR No.
        178727-1 in the Kingdom of Bahrain, with its registered office at 302, 3rd
        Floor, Bahrain Fintech Bay, Arcapita Building 551, Road 4612, Block 346,
        Manama, Kingdom of Bahrain.
      </p>
      <h3>1.2</h3>
      <p>
        This Privacy Policy describes how Amanat collects, uses, stores, discloses,
        and protects Personal Data in relation to all our services, including: the
        Amanat mobile application, website (
        <a
          href="https://www.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.amanatfintech.com
        </a>
        ), eStore (
        <a
          href="https://ecart.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          ecart.amanatfintech.com
        </a>
        ), AI Training Courses, Voice Boat, Micro-Insurance services, Doorstep Cash
        Remittances Service, Expense Tracking, Financial Well-being tools, and any
        other products or services (collectively, &apos;Services&apos;).
      </p>
      <h3>1.3</h3>
      <p>
        This Policy applies to all users, customers, eStore buyers, course enrollees,
        and visitors accessing our Services through mobile applications, web browsers,
        or any other digital interface.
      </p>
      <h3>1.4</h3>
      <p>
        By registering an account, using our Services, or accessing our eStore, you
        acknowledge that you have read, understood, and agree to this Privacy Policy.
        If you do not agree, please do not use our Services.
      </p>

      <h2>2. Data Controller and Contact Details</h2>
      <h3>2.1</h3>
      <p>
        For the purposes of Bahrain Personal Data Protection Law (PDPL 2018, Decree
        No. 30/2018), Amanat acts as the Data Controller in respect of Personal Data
        collected through our Services.
      </p>
      <h3>2.2 Data Controller Details</h3>
      <ul>
        <li>
          <strong>Entity Name:</strong> Colca Services W.L.L (Amanat)
        </li>
        <li>
          <strong>CR Number:</strong> 178727-1
        </li>
        <li>
          <strong>Address:</strong> 302, 3rd Floor, Bahrain Fintech Bay, Arcapita
          Building 551, Road 4612, Block 346, Manama, Kingdom of Bahrain
        </li>
        <li>
          <strong>Phone:</strong>{" "}
          <a href="tel:+97336961804">+973 3696 1804</a>
        </li>
        <li>
          <strong>Data Protection Contact / Privacy Officer:</strong>{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>
        </li>
        <li>
          <strong>General Enquiries:</strong>{" "}
          <a href="mailto:info@amanatfintech.com">info@amanatfintech.com</a>
        </li>
        <li>
          <strong>Grievance Officer:</strong>{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>
        </li>
      </ul>
      <h3>2.3</h3>
      <p>
        Data subjects may exercise their rights under Section 8 by writing to{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> with
        the subject line &apos;Data Subject Request&apos;.
      </p>

      <h2>3. Personal Data We Collect</h2>
      <h3>3.1 Identity and Account Data</h3>
      <p>
        Full name, date of birth, nationality, CPR ID number, passport number,
        government-issued identification documents, profile photographs, username, and
        account credentials.
      </p>
      <h3>3.2 Contact Data</h3>
      <p>
        Residential address, email address, mobile phone number, and WhatsApp contact
        number.
      </p>
      <h3>3.3 Financial and Transaction Data</h3>
      <p>
        Bank account details (where provided for refunds), payment card information
        processed through PCI-DSS compliant third-party processors (Amanat does not
        store card numbers), transaction history, course purchase records,
        micro-insurance premiums, and refund records.
      </p>
      <h3>3.4 eCart and Purchase Data</h3>
      <p>
        Items added to shopping cart, purchase orders, payment method type (Visa,
        Mastercard, BenefitPay), order confirmation details, and eCart browsing history
        on{" "}
        <a
          href="https://ecart.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          ecart.amanatfintech.com
        </a>
        .
      </p>
      <h3>3.5 KYC and AML/CFT Data</h3>
      <p>
        Identity verification documents, biometric data where applicable (processed by
        our KYC provider — see Section 7.2(d)), politically exposed person (PEP)
        screening results, sanctions screening data, beneficial ownership information,
        and enhanced due diligence records. Collected in accordance with Bahrain Law
        No. 4/2001 and CBB AML/CFT Module (FC-1 to FC-7). Where biometric data (such as
        facial recognition scans) is collected for identity verification, this is
        processed on the basis of your explicit consent or as required by law for
        AML/CFT compliance. Biometric data is subject to the 5-year AML/KYC retention
        schedule.
      </p>
      <h3>3.6 Technical and Usage Data</h3>
      <p>
        IP address, device type, operating system, browser type, app version, session
        duration, pages accessed, click-stream data, crash logs, and error reports.
      </p>
      <h3>3.7 Course and Learning Data</h3>
      <p>
        Course enrolment records, assessment scores, certificates issued, learning
        progress, AI interaction logs, and completion status.
      </p>
      <h3>3.8 Insurance Data</h3>
      <p>
        Policy details, beneficiary information, claim history, health-related
        declarations (where applicable for micro-insurance products), and insurance
        premium payment records.
      </p>
      <h3>3.9 Communications Data</h3>
      <p>
        Records of correspondence with our support team, feedback, complaints, and
        survey responses.
      </p>
      <h3>3.10 Cookie and Tracking Data</h3>
      <p>As described in Section 11 (Cookie Policy).</p>
      <h3>3.11 Doorstep Cash Remittance Recipient Data</h3>
      <p>
        For Doorstep Cash Remittance Services, we share your first name, first initial
        of your surname, and photograph with our local delivery partner in the recipient
        country (India/Nepal) for identity verification at the point of cash delivery.
        Such partners are bound by data processing agreements and are only permitted to
        use this information for the delivery purpose.
      </p>

      <h2>4. How We Collect Personal Data</h2>
      <h3>4.1 Directly from you</h3>
      <p>
        When you register an account, complete KYC verification, make a purchase on our
        eCart, enrol in a course, apply for micro-insurance, contact our support team,
        complete surveys, or submit any form on our platforms.
      </p>
      <h3>4.2 Automatically</h3>
      <p>
        When you use our mobile application, visit our website or eCart, your device
        transmits technical data including IP address, device identifiers, and usage
        logs automatically.
      </p>
      <h3>4.3 From third parties</h3>
      <p>
        Government databases (CPR verification), payment processors, sanctions screening
        providers, insurance partners, and KYC verification service providers, where
        permitted by applicable law.
      </p>
      <h3>4.4 From cookies and similar technologies</h3>
      <p>As described in Section 11 of this Policy.</p>

      <h2>5. Legal Basis and Purposes for Processing</h2>
      <p>
        Under the Bahrain PDPL 2018 (Decree No. 30/2018), we process your Personal Data
        on the following legal bases:
      </p>
      <ul>
        <li>
          <strong>Consent:</strong> For marketing communications, optional analytics,
          non-essential cookies, and processing of sensitive personal data (including
          health information for insurance products and biometric data for KYC). You may
          withdraw consent at any time as described in Section 8.7.
        </li>
        <li>
          <strong>Contractual Necessity:</strong> To create and manage your account,
          process purchases, deliver eCart orders, provide course access, administer
          micro-insurance policies, process refunds, and perform remittance transactions.
        </li>
        <li>
          <strong>Legal Obligation:</strong> To fulfil KYC/AML/CFT requirements under
          Bahrain Law No. 4/2001, CBB AML/CFT Module (FC-1 to FC-7), FATF
          Recommendations, and MENAFATF obligations; to maintain financial records as
          required by regulatory authorities; to respond to lawful requests from
          competent authorities.
        </li>
        <li>
          <strong>Legitimate Interests:</strong> For fraud prevention, security
          monitoring, improving our Services, aggregated/anonymised internal analytics,
          and defending against legal claims — provided such interests do not override
          your fundamental rights.
        </li>
        <li>
          <strong>Vital Interests:</strong> In emergency circumstances, to protect the
          life or physical safety of a data subject or another person.
        </li>
      </ul>
      <p>
        We will not process your Personal Data for purposes incompatible with those
        stated above without obtaining fresh consent or where otherwise permitted by
        law.
      </p>

      <h2>6. Data Retention Schedule</h2>
      <h3>6.1</h3>
      <p>
        We retain Personal Data only as long as necessary for the stated purpose, or as
        required by law. The following retention schedule applies:
      </p>
      <div className="legal-table-wrap">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Data Type</th>
              <th>Retention Period</th>
              <th>Legal Basis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>AML/KYC records &amp; identity documents</td>
              <td>5 years post-relationship end</td>
              <td>CBB AML Module / Law 4/2001</td>
            </tr>
            <tr>
              <td>
                Transaction records (Doorstep Cash Delivery request, purchases)
              </td>
              <td>5 years</td>
              <td>CBB Rulebook / regulatory requirement</td>
            </tr>
            <tr>
              <td>Course purchase &amp; enrolment records</td>
              <td>3 years</td>
              <td>Contract law / consumer protection</td>
            </tr>
            <tr>
              <td>Micro-insurance records</td>
              <td>Policy term + 5 years</td>
              <td>CBB Insurance Module</td>
            </tr>
            <tr>
              <td>Marketing consent records</td>
              <td>Until consent withdrawn + 1 year</td>
              <td>PDPL Art. 7 / audit trail</td>
            </tr>
            <tr>
              <td>Technical / log data</td>
              <td>12 months</td>
              <td>Legitimate interests / security</td>
            </tr>
            <tr>
              <td>Support correspondence &amp; complaints</td>
              <td>2 years from resolution</td>
              <td>Legitimate interests</td>
            </tr>
            <tr>
              <td>Biometric data (KYC verification)</td>
              <td>Duration of KYC verification process</td>
              <td>Legal obligation / consent</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h3>6.2</h3>
      <p>
        After the applicable retention period expires, Personal Data will be securely
        deleted or anonymised, unless retention is required by a court order, regulatory
        directive, or ongoing investigation.
      </p>
      <h3>6.3</h3>
      <p>
        Where you request deletion of your account (see Section 8.3), we will erase your
        personal profile data within 30 days, subject to retention obligations in
        Section 6.1 (which prevail over deletion requests where legally required).
      </p>

      <h2>7. Sharing and Disclosure of Personal Data</h2>
      <h3>7.1</h3>
      <p>
        We do not sell, rent, or trade your Personal Data to third parties for their own
        marketing purposes.
      </p>
      <h3>7.2</h3>
      <p>
        We may share your Personal Data with the following categories of recipients,
        strictly on a need-to-know basis:
      </p>
      <ul>
        <li>
          <strong>(a) Technology and Service Providers:</strong> Our technology
          development partner FIA Global (India) provides platform engineering and
          hosting services on Digital Ocean / Amazon Web Services (AWS). FIA Global acts
          as a Data Processor under our Data Processing Agreement (DPA), and cross-border
          transfers to India are governed by Standard Contractual Clauses (SCCs).
        </li>
        <li>
          <strong>(b) Payment Processors:</strong> PCI-DSS certified payment gateway
          providers (processing Visa, Mastercard, and BenefitPay transactions). Bound by
          strict data processing agreements.
        </li>
        <li>
          <strong>(c) Insurance Partners:</strong> Micro-insurance product partners who
          require your personal data to underwrite and administer insurance policies.
          Identified in your policy document. Regulated entities bound by applicable data
          protection obligations.
        </li>
        <li>
          <strong>(d) KYC and Compliance Providers:</strong> Identity verification and
          sanctions screening service providers. A list of current data processors is
          available on request via{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>.
        </li>
        <li>
          <strong>(e) Regulatory and Law Enforcement Authorities:</strong> The Central
          Bank of Bahrain (CBB), Financial Intelligence Directorate (FID), Ministry of
          Interior (MOI), Public Prosecution, MOICT, or any other competent authority
          where required by law, court order, or regulatory direction.
        </li>
        <li>
          <strong>(f) Doorstep Cash Delivery Agents:</strong> For Doorstep Remittance
          Services, limited sender identification data shared with local delivery agents
          in India/Nepal solely for delivery verification (see Section 3.11).
        </li>
        <li>
          <strong>(g) Professional Advisers:</strong> Legal counsel, auditors, and
          accountants, subject to professional confidentiality obligations.
        </li>
        <li>
          <strong>(h) Business Transferees:</strong> In the event of a merger,
          acquisition, or sale of the business, your data may be transferred to the
          acquiring entity, subject to equivalent privacy protections and notification to
          you.
        </li>
      </ul>

      <h2>8. Your Data Subject Rights</h2>
      <p>
        Under the Bahrain Personal Data Protection Law (PDPL 2018, Decree No. 30/2018),
        you have the following rights in relation to your Personal Data:
      </p>
      <h3>8.1 Right of Access</h3>
      <p>
        You may request a copy of the Personal Data we hold about you and information on
        how it is processed.
      </p>
      <h3>8.2 Right to Correction</h3>
      <p>
        You may request correction of inaccurate or incomplete Personal Data. Certain
        data can be updated directly through your in-app profile settings.
      </p>
      <h3>8.3 Right to Erasure (Right to be Forgotten)</h3>
      <p>
        You may request deletion of your account and associated personal profile data by
        emailing{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>{" "}
        (Subject: &apos;Account Deletion Request&apos;) or using the in-app deletion
        function. We will process your request within 30 days. Certain data must be
        retained by law (see Section 6) and cannot be deleted upon request.
      </p>
      <h3>8.4 Right to Object</h3>
      <p>
        You may object to processing based on legitimate interests, and to direct
        marketing at any time.
      </p>
      <h3>8.5 Right to Restrict Processing</h3>
      <p>
        You may request restriction of processing in certain circumstances, for example
        where you contest the accuracy of the data.
      </p>
      <h3>8.6 Right to Data Portability</h3>
      <p>
        Where processing is based on consent or contractual necessity and is automated,
        you may request your Personal Data in a structured, machine-readable format
        (e.g., CSV or JSON).
      </p>
      <h3>8.7 Right to Withdraw Consent</h3>
      <p>
        You may withdraw consent at any time via{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> or
        in-app consent settings. Withdrawal does not affect the lawfulness of prior
        processing.
      </p>
      <h3>8.8 Right to Lodge a Complaint</h3>
      <p>
        If you believe we have violated the PDPL 2018, you may lodge a complaint with
        the supervisory authority designated under the PDPL 2018 in the Kingdom of
        Bahrain, and/or seek compensation for damages.
      </p>
      <h3>8.9 How to Exercise Rights</h3>
      <p>
        Email{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> with
        subject &apos;Data Subject Request — [Right Name]&apos;. We will respond within
        30 days. In complex cases, we may extend by a further 30 days with notification
        to you. Identity verification may be required before processing your request.
      </p>

      <h2>9. Cross-Border Data Transfers</h2>
      <h3>9.1</h3>
      <p>
        Your Personal Data is primarily stored and processed within the Kingdom of
        Bahrain and on cloud infrastructure operated by DigitalOcean (NYC3 region,
        United States). Where data is transferred internationally, appropriate safeguards
        are in place.
      </p>
      <h3>9.2</h3>
      <p>
        Our technology development partner FIA Global (India) processes Personal Data
        for platform development, maintenance, and support, governed by a Data
        Processing Agreement incorporating Standard Contractual Clauses (SCCs) ensuring
        adequate protection equivalent to Bahrain PDPL 2018 requirements.
      </p>
      <h3>9.3</h3>
      <p>
        For Doorstep Cash Remittance Services, limited sender identification data is
        shared with licensed local delivery agents in India and Nepal under contractual
        data protection obligations.
      </p>
      <h3>9.4</h3>
      <p>
        Where we transfer Personal Data outside Bahrain to countries without equivalent
        data protection legislation, we ensure appropriate safeguards: (a) Standard
        Contractual Clauses (SCCs); (b) binding corporate rules; (c) contractual
        provisions imposing equivalent obligations; or (d) explicit consent where
        applicable.
      </p>
      <h3>9.5</h3>
      <p>
        You may request information about the safeguards applicable to cross-border
        transfers by contacting{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>.
      </p>

      <h2>10. Data Breach Notification</h2>
      <h3>10.1</h3>
      <p>
        Amanat maintains an Information Security Policy and incident response procedures
        designed to protect your Personal Data.
      </p>
      <h3>10.2</h3>
      <p>
        In the event of a Personal Data Breach likely to result in a risk to the rights
        and freedoms of individuals, Amanat will, in accordance with PDPL 2018:
      </p>
      <ul>
        <li>
          <strong>(a)</strong> Notify the competent supervisory authority within
          seventy-two (72) hours of becoming aware of the breach, providing: nature of
          breach, categories of data subjects affected, likely consequences, and measures
          taken.
        </li>
        <li>
          <strong>(b)</strong> Notify affected data subjects without undue delay where
          the breach is likely to result in high risk to their rights and freedoms.
        </li>
        <li>
          <strong>(c)</strong> Maintain an internal Personal Data Breach Register
          documenting all breaches, their effects, and remedial actions taken.
        </li>
      </ul>
      <h3>10.3</h3>
      <p>
        If you believe your account has been compromised, notify us immediately at{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>{" "}
        (Subject: &apos;Security Incident&apos;).
      </p>

      <h2>11. Cookie Policy</h2>
      <h3>11.1</h3>
      <p>
        Our website (
        <a
          href="https://www.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.amanatfintech.com
        </a>
        ) and eStore (
        <a
          href="https://ecart.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          ecart.amanatfintech.com
        </a>
        ) use cookies and similar tracking technologies to enhance your experience,
        maintain your session, and analyse usage.
      </p>
      <h3>11.2 Types of Cookies We Use</h3>
      <ul>
        <li>
          <strong>(a) Essential / Strictly Necessary Cookies:</strong> Required for
          website and eStore operation (session management, authentication, security).
          Cannot be disabled without impacting functionality.
        </li>
        <li>
          <strong>(b) Performance / Analytics Cookies:</strong> Used to understand
          visitor interactions. Aggregated, anonymised data. Your consent is required.
        </li>
        <li>
          <strong>(c) Functional Cookies:</strong> Remember your preferences (language,
          region, display settings). Your consent is required for non-essential
          functional cookies.
        </li>
        <li>
          <strong>(d) Marketing / Targeting Cookies:</strong> Placed only with your
          explicit consent. Withdrawal available at any time.
        </li>
      </ul>
      <h3>11.3 Cookie Consent</h3>
      <p>
        When you first visit our website or eStore, a cookie consent banner is
        displayed. You may accept all cookies, accept essential cookies only, or
        customise your preferences. You can also manage cookies through your browser
        settings; however, disabling essential cookies may affect platform
        functionality.
      </p>
      <h3>11.4 Third-Party Cookies</h3>
      <p>
        Our payment gateway providers and analytics partners may place their own cookies
        on your device. These are subject to the respective third-party privacy
        policies.
      </p>
      <h3>11.5 Session Management</h3>
      <p>
        Our mobile application uses session tokens rather than browser cookies. Session
        tokens expire after 24 hours of inactivity.
      </p>

      <h2>12. Information Security</h2>
      <h3>12.1</h3>
      <p>
        Amanat implements appropriate technical and organisational security measures to
        protect Personal Data, including:
      </p>
      <ul>
        <li>
          <strong>(a)</strong> Encryption of data in transit using TLS/SSL (minimum TLS
          1.2) and encryption of sensitive data at rest.
        </li>
        <li>
          <strong>(b)</strong> Role-based access controls (RBAC) restricting access to
          Personal Data to authorised personnel only.
        </li>
        <li>
          <strong>(c)</strong> Multi-factor authentication (MFA) for administrative
          access to systems storing Personal Data.
        </li>
        <li>
          <strong>(d)</strong> Regular security assessments, penetration testing, and
          vulnerability scanning.
        </li>
        <li>
          <strong>(e)</strong> PCI-DSS compliant payment processing — card data is never
          stored on Amanat systems.
        </li>
        <li>
          <strong>(f)</strong> Staff training on data protection and information
          security best practices.
        </li>
      </ul>
      <h3>12.2</h3>
      <p>
        Despite our best efforts, no system is entirely secure. We cannot guarantee
        absolute security of data transmitted over the internet. Please use a strong
        password, do not share your login credentials, and log out when not in use.
      </p>

      <h2>13. AML/CFT Data Processing</h2>
      <h3>13.1</h3>
      <p>
        Amanat implements Know Your Customer (KYC), Customer Due Diligence (CDD), and
        Enhanced Due Diligence (EDD) procedures in accordance with Bahrain Anti-Money
        Laundering Law No. 4/2001, the CBB AML/CFT Module (Modules FC-1 through FC-7),
        and the Financial Action Task Force (FATF) Recommendations and MENAFATF mutual
        evaluation obligations applicable to the Kingdom of Bahrain.
      </p>
      <h3>13.2</h3>
      <p>For AML/CFT compliance purposes, we process and store:</p>
      <ul>
        <li>Identity verification documents and biometric data where applicable.</li>
        <li>
          Politically Exposed Person (PEP) status and screening results.
        </li>
        <li>
          Sanctions screening results against UN, OFAC, HMT, and other applicable
          sanctions lists.
        </li>
        <li>
          Transaction monitoring records and suspicious transaction/activity reports.
        </li>
        <li>
          Source of funds declarations and supporting documentation.
        </li>
      </ul>
      <h3>13.3 Tipping-Off Prohibition</h3>
      <p>
        Under applicable AML/CFT laws, we are legally prohibited from notifying you (a
        &apos;tipping-off&apos; prohibition) if we have filed or are considering filing a
        Suspicious Transaction Report (STR) or Suspicious Activity Report (SAR) with the
        Financial Intelligence Directorate (FID) of Bahrain. The right to erasure
        (Section 8.3) does not apply to AML/CFT data where retention is mandated by law.
      </p>

      <h2>14. Children&apos;s Privacy</h2>
      <h3>14.1</h3>
      <p>
        Our Services are intended for individuals aged 18 years and above, in accordance
        with the Bahrain Civil Code (Legislative Decree No. 19/2001) and applicable
        financial services regulations. We do not knowingly collect Personal Data from
        individuals under the age of 18 years.
      </p>
      <h3>14.2</h3>
      <p>
        If we become aware that Personal Data of an individual under 18 has been
        collected without appropriate consent, we will promptly delete such data. If you
        are a parent or guardian and believe we have collected your child&apos;s data,
        please contact{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>{" "}
        immediately.
      </p>

      <h2>15. Marketing Communications</h2>
      <h3>15.1</h3>
      <p>
        With your prior explicit consent, we may send you marketing communications about
        our products, services, courses, promotions, and updates via email, SMS, and
        in-app notifications.
      </p>
      <h3>15.2</h3>
      <p>
        You may opt out of marketing communications at any time by: (a) clicking the
        &apos;Unsubscribe&apos; link in any marketing email; (b) adjusting your
        notification preferences in the app settings; (c) contacting{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>. Opting
        out will not affect transactional or service-related communications (e.g.,
        account alerts, receipts, security notifications).
      </p>

      <h2>16. Governing Law and Jurisdiction</h2>
      <h3>16.1</h3>
      <p>
        This Privacy Policy is governed by and construed in accordance with the laws of
        the Kingdom of Bahrain, including the Personal Data Protection Law (PDPL 2018,
        Decree No. 30/2018), Bahrain Anti-Money Laundering Law (Law No. 4/2001), and the
        CBB Rulebook.
      </p>
      <h3>16.2</h3>
      <p>
        Any dispute arising from or in connection with this Privacy Policy shall be
        subject to the exclusive jurisdiction of the courts of the Kingdom of Bahrain.
      </p>
      <h3>16.3</h3>
      <p>
        If any provision of this Privacy Policy is found to be invalid or unenforceable,
        the remaining provisions shall continue to be valid and enforceable.
      </p>

      <h2>17. Changes to This Privacy Policy</h2>
      <h3>17.1</h3>
      <p>
        Amanat reserves the right to update or amend this Privacy Policy at any time to
        reflect changes in our practices, applicable law, or regulatory requirements.
      </p>
      <h3>17.2</h3>
      <p>
        We will notify you of material changes by: (a) posting an updated Privacy Policy
        with a new &apos;Last Revised&apos; date; (b) sending an in-app notification or
        email alert where changes significantly affect your rights.
      </p>
      <h3>17.3</h3>
      <p>
        Your continued use of our Services after the effective date of any update
        constitutes acceptance of the revised Privacy Policy.
      </p>

      <h2>18. Bilingual Clause / بند ثنائي اللغة</h2>
      <h3>18.1</h3>
      <p>
        This Privacy Policy is prepared in both English and Arabic. In the event of any
        inconsistency or conflict between the English and Arabic versions, the Arabic
        version shall prevail in accordance with the laws of the Kingdom of Bahrain.
      </p>
      <h3>18.2</h3>
      <p dir="rtl" className="legal-rtl">
        تم إعداد سياسة الخصوصية هذه باللغتين الإنجليزية والعربية. في حالة أي تناقض أو
        تعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية وفقاً لقوانين مملكة
        البحرين.
      </p>

      <hr className="legal-divider" />

      <div className="legal-rtl">
        <div className="legal-doc-header">
          <p className="legal-doc-brand">أمانات</p>
          <p className="legal-doc-company">
            شركة كولكا سيرفيسز ذ.م.م — سجل تجاري رقم 178727-1 — مملكة البحرين
          </p>
          <h1 className="legal-doc-title">سياسة الخصوصية</h1>
          <p className="legal-doc-version">الإصدار 2</p>
          <p className="legal-doc-dates">
            تاريخ السريان: 1 يوليو 2025 | تاريخ آخر مراجعة: 1 أغسطس 2026
          </p>
        </div>

        <h2>1. المقدمة والنطاق</h2>
        <h3>1.1</h3>
        <p>
          شركة كولكا سيرفيسز ذ.م.م، والمتاجرة تحت اسم أمانات (&quot;أمانات&quot;،
          &quot;نحن&quot;، &quot;لنا&quot;، &quot;الخاص بنا&quot;)، هي شركة مسجَّلة حسب
          الأصول تحت السجل التجاري رقم 178727-1 في مملكة البحرين، ويقع مقرها المسجَّل في
          302، الطابق الثالث، خليج البحرين للتكنولوجيا المالية، مبنى أركابيتا 551، طريق
          4612، مجمع 346، المنامة، مملكة البحرين.
        </p>
        <h3>1.2</h3>
        <p>
          توضِّح سياسة الخصوصية هذه كيفية قيام أمانات بجمع البيانات الشخصية واستخدامها
          وتخزينها والإفصاح عنها وحمايتها فيما يتعلق بجميع خدماتنا، بما في ذلك: تطبيق
          أمانات للهواتف المحمولة، والموقع الإلكتروني (
          <a
            href="https://www.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.amanatfintech.com
          </a>
          )، والمتجر الإلكتروني (
          <a
            href="https://ecart.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecart.amanatfintech.com
          </a>
          )، ودورات التدريب على الذكاء الاصطناعي، وVoice Boat، وخدمات التأمين متناهي
          الصغر، وخدمة توصيل الحوالات النقدية إلى المنزل، وتتبع المصروفات، وأدوات الرفاه
          المالي، وأي منتجات أو خدمات أخرى (يُشار إليها مجتمعةً بـ
          &quot;الخدمات&quot;).
        </p>
        <h3>1.3</h3>
        <p>
          تسري هذه السياسة على جميع المستخدمين والعملاء ومشتري المتجر الإلكتروني
          والمسجَّلين في الدورات والزوار الذين يصلون إلى خدماتنا من خلال تطبيقات الهواتف
          المحمولة أو متصفحات الويب أو أية واجهة رقمية أخرى.
        </p>
        <h3>1.4</h3>
        <p>
          بتسجيلك لحساب، أو استخدامك لخدماتنا، أو دخولك إلى متجرنا الإلكتروني، فإنك تُقر
          بأنك قد قرأت سياسة الخصوصية هذه وفهمتها وتوافق عليها. وإذا كنت لا توافق عليها،
          فيُرجى عدم استخدام خدماتنا.
        </p>

        <h2>2. مدير البيانات وبيانات الاتصال</h2>
        <h3>2.1</h3>
        <p>
          لأغراض قانون حماية البيانات الشخصية البحريني (قانون حماية البيانات الشخصية لسنة
          2018، المرسوم رقم 30/2018)، تعمل أمانات بصفتها مدير البيانات فيما يتعلق
          بالبيانات الشخصية التي يتم جمعها من خلال خدماتنا.
        </p>
        <h3>2.2 بيانات مدير البيانات</h3>
        <ul>
          <li>
            <strong>اسم الكيان:</strong> شركة كولكا سيرفيسز ذ.م.م (أمانات)
          </li>
          <li>
            <strong>رقم السجل التجاري:</strong> 178727-1
          </li>
          <li>
            <strong>العنوان:</strong> 302، الطابق الثالث، خليج البحرين للتكنولوجيا
            المالية، مبنى أركابيتا 551، طريق 4612، مجمع 346، المنامة، مملكة البحرين
          </li>
          <li>
            <strong>الهاتف:</strong>{" "}
            <a href="tel:+97336961804">+973 3696 1804</a>
          </li>
          <li>
            <strong>جهة الاتصال المعنية بحماية البيانات / مسؤول الخصوصية:</strong>{" "}
            <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>
          </li>
          <li>
            <strong>الاستفسارات العامة:</strong>{" "}
            <a href="mailto:info@amanatfintech.com">info@amanatfintech.com</a>
          </li>
          <li>
            <strong>مسؤول التظلمات:</strong>{" "}
            <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>
          </li>
        </ul>
        <h3>2.3</h3>
        <p>
          يجوز لأصحاب البيانات ممارسة حقوقهم المقرَّرة بموجب القسم 8 بالكتابة إلى{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> مع
          كتابة عبارة &quot;طلب صاحب بيانات&quot; في خانة الموضوع.
        </p>

        <h2>3. البيانات الشخصية التي نجمعها</h2>
        <h3>3.1 بيانات الهوية والحساب</h3>
        <p>
          الاسم الكامل، وتاريخ الميلاد، والجنسية، ورقم البطاقة الشخصية (CPR)، ورقم جواز
          السفر، ووثائق الهوية الصادرة عن الجهات الحكومية، والصور الشخصية، واسم المستخدم،
          وبيانات اعتماد الحساب.
        </p>
        <h3>3.2 بيانات الاتصال</h3>
        <p>
          عنوان السكن، وعنوان البريد الإلكتروني، ورقم الهاتف المحمول، ورقم التواصل عبر
          واتساب.
        </p>
        <h3>3.3 البيانات المالية وبيانات المعاملات</h3>
        <p>
          تفاصيل الحساب المصرفي (حيثما تُقدَّم لأغراض الاسترداد)، ومعلومات بطاقة الدفع التي
          تتم معالجتها من خلال جهات معالجة خارجية ملتزمة بمعيار PCI-DSS (لا تحتفظ أمانات
          بأرقام البطاقات)، وسجل المعاملات، وسجلات شراء الدورات، وأقساط التأمين متناهي
          الصغر، وسجلات الاسترداد.
        </p>
        <h3>3.4 بيانات المتجر الإلكتروني وعمليات الشراء</h3>
        <p>
          العناصر المضافة إلى سلة التسوق، وطلبات الشراء، ونوع وسيلة الدفع (فيزا،
          ماستركارد، بنفت باي)، وتفاصيل تأكيد الطلب، وسجل التصفُّح في المتجر الإلكتروني
          على{" "}
          <a
            href="https://ecart.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecart.amanatfintech.com
          </a>
          .
        </p>
        <h3>3.5 بيانات اعرف عميلك ومكافحة غسل الأموال وتمويل الإرهاب</h3>
        <p>
          وثائق التحقُّق من الهوية، والبيانات البيومترية حيثما ينطبق ذلك (التي تتم معالجتها
          من قِبَل مزوِّد خدمة اعرف عميلك لدينا — انظر القسم 7.2(د))، ونتائج فحص الأشخاص
          المعرَّضين سياسياً (PEP)، وبيانات فحص العقوبات، ومعلومات المستفيد الحقيقي، وسجلات
          العناية الواجبة المعزَّزة. ويتم جمعها وفقاً لقانون مملكة البحرين رقم 4/2001 ووحدة
          مكافحة غسل الأموال وتمويل الإرهاب الصادرة عن مصرف البحرين المركزي (من FC-1 إلى
          FC-7). وحيثما يتم جمع بيانات بيومترية (مثل عمليات مسح التعرُّف على الوجه) لأغراض
          التحقُّق من الهوية، تتم معالجتها استناداً إلى موافقتك الصريحة أو وفقاً لما يقتضيه
          القانون للامتثال لمتطلبات مكافحة غسل الأموال وتمويل الإرهاب. وتخضع البيانات
          البيومترية لجدول الاحتفاظ لمدة 5 سنوات الخاص بمكافحة غسل الأموال/اعرف عميلك.
        </p>
        <h3>3.6 البيانات التقنية وبيانات الاستخدام</h3>
        <p>
          عنوان بروتوكول الإنترنت (IP)، ونوع الجهاز، ونظام التشغيل، ونوع المتصفح، وإصدار
          التطبيق، ومدة الجلسة، والصفحات التي تم الوصول إليها، وبيانات تتبُّع النقرات،
          وسجلات الأعطال، وتقارير الأخطاء.
        </p>
        <h3>3.7 بيانات الدورات والتعلُّم</h3>
        <p>
          سجلات التسجيل في الدورات، ودرجات التقييم، والشهادات الصادرة، وتقدُّم التعلُّم،
          وسجلات التفاعل مع الذكاء الاصطناعي، وحالة الإتمام.
        </p>
        <h3>3.8 بيانات التأمين</h3>
        <p>
          تفاصيل الوثيقة، ومعلومات المستفيدين، وسجل المطالبات، والإقرارات المتعلقة بالحالة
          الصحية (حيثما ينطبق ذلك على منتجات التأمين متناهي الصغر)، وسجلات سداد أقساط
          التأمين.
        </p>
        <h3>3.9 بيانات المراسلات</h3>
        <p>
          سجلات المراسلات مع فريق الدعم لدينا، والملاحظات، والشكاوى، وردود الاستبيانات.
        </p>
        <h3>3.10 بيانات ملفات تعريف الارتباط والتتبُّع</h3>
        <p>على النحو الموضَّح في القسم 11 (سياسة ملفات تعريف الارتباط).</p>
        <h3>3.11 بيانات مستلمي الحوالات النقدية إلى المنزل</h3>
        <p>
          بالنسبة لخدمات توصيل الحوالات النقدية إلى المنزل، نشارك اسمك الأول والحرف الأول
          من اسم عائلتك وصورتك الفوتوغرافية مع شريك التوصيل المحلي لدينا في بلد المستلم
          (الهند/نيبال) لأغراض التحقُّق من الهوية عند نقطة تسليم النقد. ويلتزم هؤلاء الشركاء
          باتفاقيات معالجة البيانات ولا يُسمح لهم باستخدام هذه المعلومات إلا لغرض التوصيل.
        </p>

        <h2>4. كيفية جمعنا للبيانات الشخصية</h2>
        <h3>4.1 مباشرةً منك</h3>
        <p>
          عند تسجيل حساب، أو إتمام إجراءات التحقُّق من الهوية (اعرف عميلك)، أو إجراء عملية
          شراء على متجرنا الإلكتروني، أو التسجيل في دورة، أو التقدُّم بطلب للحصول على تأمين
          متناهي الصغر، أو التواصل مع فريق الدعم لدينا، أو استكمال الاستبيانات، أو تقديم
          أي نموذج على منصاتنا.
        </p>
        <h3>4.2 تلقائياً</h3>
        <p>
          عند استخدامك لتطبيقنا على الهاتف المحمول، أو زيارتك لموقعنا الإلكتروني أو متجرنا
          الإلكتروني، يقوم جهازك تلقائياً بإرسال بيانات تقنية تشمل عنوان بروتوكول الإنترنت
          (IP) ومعرِّفات الجهاز وسجلات الاستخدام.
        </p>
        <h3>4.3 من أطراف ثالثة</h3>
        <p>
          قواعد البيانات الحكومية (التحقُّق من البطاقة الشخصية CPR)، وجهات معالجة المدفوعات،
          ومزوِّدي خدمات فحص العقوبات، وشركاء التأمين، ومزوِّدي خدمات التحقُّق من الهوية (اعرف
          عميلك)، وذلك حيثما يسمح القانون المعمول به بذلك.
        </p>
        <h3>4.4 من ملفات تعريف الارتباط والتقنيات المشابهة</h3>
        <p>على النحو الموضَّح في القسم 11 من هذه السياسة.</p>

        <h2>5. الأساس القانوني وأغراض المعالجة</h2>
        <p>
          بموجب قانون حماية البيانات الشخصية البحريني لسنة 2018 (المرسوم رقم 30/2018)،
          نقوم بمعالجة بياناتك الشخصية استناداً إلى الأسس القانونية التالية:
        </p>
        <ul>
          <li>
            <strong>الموافقة:</strong> للمراسلات التسويقية، والتحليلات الاختيارية، وملفات
            تعريف الارتباط غير الضرورية، ومعالجة البيانات الشخصية الحسَّاسة (بما في ذلك
            المعلومات الصحية الخاصة بمنتجات التأمين والبيانات البيومترية الخاصة بإجراءات
            اعرف عميلك). ويجوز لك سحب موافقتك في أي وقت على النحو الموضَّح في القسم 8.7.
          </li>
          <li>
            <strong>الضرورة التعاقدية:</strong> لإنشاء حسابك وإدارته، ومعالجة عمليات
            الشراء، وتسليم طلبات المتجر الإلكتروني، وإتاحة الوصول إلى الدورات، وإدارة
            وثائق التأمين متناهي الصغر، ومعالجة عمليات الاسترداد، وتنفيذ معاملات التحويل
            المالي.
          </li>
          <li>
            <strong>الالتزام القانوني:</strong> للوفاء بمتطلبات اعرف عميلك ومكافحة غسل
            الأموال وتمويل الإرهاب بموجب قانون مملكة البحرين رقم 4/2001، ووحدة مكافحة غسل
            الأموال وتمويل الإرهاب الصادرة عن مصرف البحرين المركزي (من FC-1 إلى FC-7)،
            وتوصيات مجموعة العمل المالي (FATF)، والتزامات مجموعة العمل المالي لمنطقة الشرق
            الأوسط وشمال أفريقيا (MENAFATF)؛ وللاحتفاظ بالسجلات المالية على النحو الذي
            تقتضيه الجهات الرقابية؛ وللاستجابة للطلبات المشروعة الصادرة عن الجهات
            المختصة.
          </li>
          <li>
            <strong>المصالح المشروعة:</strong> لمنع الاحتيال، ومراقبة الأمن، وتحسين
            خدماتنا، والتحليلات الداخلية المجمَّعة/مجهولة الهوية، والدفاع في مواجهة المطالبات
            القانونية — شريطة ألا تتجاوز هذه المصالح حقوقك الأساسية.
          </li>
          <li>
            <strong>المصالح الحيوية:</strong> في الظروف الطارئة، لحماية حياة صاحب البيانات
            أو أي شخص آخر أو سلامته الجسدية.
          </li>
        </ul>
        <p>
          ولن نقوم بمعالجة بياناتك الشخصية لأغراض تتعارض مع الأغراض المذكورة أعلاه دون
          الحصول على موافقة جديدة أو حيثما يسمح القانون بخلاف ذلك.
        </p>

        <h2>6. جدول الاحتفاظ بالبيانات</h2>
        <h3>6.1</h3>
        <p>
          نحتفظ بالبيانات الشخصية فقط للمدة اللازمة لتحقيق الغرض المذكور، أو حسبما يقتضيه
          القانون. ويسري جدول الاحتفاظ التالي:
        </p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>نوع البيانات</th>
                <th>مدة الاحتفاظ</th>
                <th>الأساس القانوني</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>سجلات مكافحة غسل الأموال/اعرف عميلك ووثائق الهوية</td>
                <td>5 سنوات بعد انتهاء العلاقة</td>
                <td>
                  وحدة مكافحة غسل الأموال الصادرة عن مصرف البحرين المركزي / القانون رقم
                  4/2001
                </td>
              </tr>
              <tr>
                <td>سجلات المعاملات (طلب توصيل النقد إلى المنزل، وعمليات الشراء)</td>
                <td>5 سنوات</td>
                <td>كتاب قواعد مصرف البحرين المركزي / متطلَّب رقابي</td>
              </tr>
              <tr>
                <td>سجلات شراء الدورات والتسجيل فيها</td>
                <td>3 سنوات</td>
                <td>قانون العقود / حماية المستهلك</td>
              </tr>
              <tr>
                <td>سجلات التأمين متناهي الصغر</td>
                <td>مدة الوثيقة + 5 سنوات</td>
                <td>وحدة التأمين الصادرة عن مصرف البحرين المركزي</td>
              </tr>
              <tr>
                <td>سجلات الموافقة التسويقية</td>
                <td>حتى سحب الموافقة + سنة واحدة</td>
                <td>المادة 7 من قانون حماية البيانات الشخصية / مسار التدقيق</td>
              </tr>
              <tr>
                <td>البيانات التقنية / بيانات السجلات</td>
                <td>12 شهراً</td>
                <td>المصالح المشروعة / الأمن</td>
              </tr>
              <tr>
                <td>مراسلات الدعم والشكاوى</td>
                <td>سنتان من تاريخ التسوية</td>
                <td>المصالح المشروعة</td>
              </tr>
              <tr>
                <td>البيانات البيومترية (التحقُّق من الهوية — اعرف عميلك)</td>
                <td>مدة عملية التحقُّق من الهوية</td>
                <td>الالتزام القانوني / الموافقة</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3>6.2</h3>
        <p>
          بعد انقضاء مدة الاحتفاظ المعمول بها، سيتم حذف البيانات الشخصية بشكل آمن أو
          جعلها مجهولة الهوية، ما لم يكن الاحتفاظ بها مطلوباً بموجب أمر قضائي أو توجيه
          رقابي أو تحقيق جارٍ.
        </p>
        <h3>6.3</h3>
        <p>
          وحيثما تطلب حذف حسابك (انظر القسم 8.3)، سنقوم بمحو بيانات ملفك الشخصي خلال 30
          يوماً، مع مراعاة التزامات الاحتفاظ المنصوص عليها في القسم 6.1 (والتي تسود على
          طلبات الحذف حيثما يكون ذلك مطلوباً قانوناً).
        </p>

        <h2>7. مشاركة البيانات الشخصية والإفصاح عنها</h2>
        <h3>7.1</h3>
        <p>
          نحن لا نبيع بياناتك الشخصية أو نؤجِّرها أو نتاجر بها لأطراف ثالثة لأغراضها
          التسويقية الخاصة.
        </p>
        <h3>7.2</h3>
        <p>
          يجوز لنا مشاركة بياناتك الشخصية مع الفئات التالية من المتسلِّمين، وذلك حصراً على
          أساس الحاجة إلى المعرفة:
        </p>
        <ul>
          <li>
            <strong>(أ) مزوِّدو التكنولوجيا والخدمات:</strong> يقدِّم شريكنا في تطوير
            التكنولوجيا FIA Global (الهند) خدمات هندسة المنصة والاستضافة على DigitalOcean
            / Amazon Web Services (AWS). وتعمل FIA Global بصفتها معالج بيانات بموجب اتفاقية
            معالجة البيانات (DPA) المبرمة معنا، وتخضع عمليات النقل عبر الحدود إلى الهند
            للشروط التعاقدية النموذجية (SCCs).
          </li>
          <li>
            <strong>(ب) جهات معالجة المدفوعات:</strong> مزوِّدو بوابات الدفع المعتمدون وفق
            معيار PCI-DSS (الذين يعالجون معاملات فيزا وماستركارد وبنفت باي). وهم ملتزمون
            باتفاقيات صارمة لمعالجة البيانات.
          </li>
          <li>
            <strong>(ج) شركاء التأمين:</strong> شركاء منتجات التأمين متناهي الصغر الذين
            يحتاجون إلى بياناتك الشخصية للاكتتاب في وثائق التأمين وإدارتها. ويتم تحديدهم في
            وثيقة التأمين الخاصة بك. وهم كيانات خاضعة للرقابة وملتزمة بالتزامات حماية
            البيانات المعمول بها.
          </li>
          <li>
            <strong>(د) مزوِّدو خدمات اعرف عميلك والامتثال:</strong> مزوِّدو خدمات التحقُّق من
            الهوية وفحص العقوبات. وتتوفر قائمة بمعالجي البيانات الحاليين عند الطلب عبر{" "}
            <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>.
          </li>
          <li>
            <strong>(هـ) الجهات الرقابية وجهات إنفاذ القانون:</strong> مصرف البحرين المركزي
            (CBB)، وإدارة المعلومات المالية (FID)، ووزارة الداخلية (MOI)، والنيابة العامة،
            ووزارة الصناعة والتجارة والسياحة (MOICT)، أو أية جهة مختصة أخرى، وذلك حيثما
            يقتضي القانون أو أمر المحكمة أو التوجيه الرقابي ذلك.
          </li>
          <li>
            <strong>(و) وكلاء توصيل النقد إلى المنزل:</strong> بالنسبة لخدمات التحويلات إلى
            المنزل، تتم مشاركة بيانات محدودة لتعريف المُرسل مع وكلاء التوصيل المحليين في
            الهند/نيبال لغرض التحقُّق من التسليم فقط (انظر القسم 3.11).
          </li>
          <li>
            <strong>(ز) المستشارون المهنيون:</strong> المستشارون القانونيون والمدقِّقون
            والمحاسبون، مع مراعاة التزامات السرية المهنية.
          </li>
          <li>
            <strong>(ح) المتنازل إليهم في الأعمال:</strong> في حالة الاندماج أو الاستحواذ أو
            بيع النشاط التجاري، يجوز نقل بياناتك إلى الكيان المستحوذ، مع مراعاة توفير حماية
            معادلة للخصوصية وإخطارك بذلك.
          </li>
        </ul>

        <h2>8. حقوقك بصفتك صاحب البيانات</h2>
        <p>
          بموجب قانون حماية البيانات الشخصية البحريني (قانون حماية البيانات الشخصية لسنة
          2018، المرسوم رقم 30/2018)، تتمتع بالحقوق التالية فيما يتعلق ببياناتك الشخصية:
        </p>
        <h3>8.1 الحق في الاطلاع</h3>
        <p>
          يجوز لك طلب نسخة من البيانات الشخصية التي نحتفظ بها عنك ومعلومات عن كيفية
          معالجتها.
        </p>
        <h3>8.2 الحق في التصحيح</h3>
        <p>
          يجوز لك طلب تصحيح البيانات الشخصية غير الدقيقة أو غير المكتملة. ويمكن تحديث
          بيانات معيَّنة مباشرةً من خلال إعدادات ملفك الشخصي داخل التطبيق.
        </p>
        <h3>8.3 الحق في المحو (الحق في النسيان)</h3>
        <p>
          يجوز لك طلب حذف حسابك وبيانات ملفك الشخصي المرتبطة به عن طريق مراسلتنا على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>{" "}
          (الموضوع: &quot;طلب حذف حساب&quot;) أو باستخدام خاصية الحذف داخل التطبيق. وسنقوم
          بمعالجة طلبك خلال 30 يوماً. ويجب الاحتفاظ ببيانات معيَّنة بموجب القانون (انظر القسم
          6) ولا يمكن حذفها بناءً على الطلب.
        </p>
        <h3>8.4 الحق في الاعتراض</h3>
        <p>
          يجوز لك الاعتراض على المعالجة القائمة على المصالح المشروعة، وعلى التسويق المباشر
          في أي وقت.
        </p>
        <h3>8.5 الحق في تقييد المعالجة</h3>
        <p>
          يجوز لك طلب تقييد المعالجة في ظروف معيَّنة، على سبيل المثال عندما تعترض على دقة
          البيانات.
        </p>
        <h3>8.6 الحق في نقل البيانات</h3>
        <p>
          حيثما تستند المعالجة إلى الموافقة أو الضرورة التعاقدية وتتم بصورة آلية، يجوز لك
          طلب بياناتك الشخصية في صيغة منظَّمة وقابلة للقراءة آلياً (مثل CSV أو JSON).
        </p>
        <h3>8.7 الحق في سحب الموافقة</h3>
        <p>
          يجوز لك سحب موافقتك في أي وقت عبر{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> أو من
          خلال إعدادات الموافقة داخل التطبيق. ولا يؤثر السحب على مشروعية المعالجة السابقة.
        </p>
        <h3>8.8 الحق في تقديم شكوى</h3>
        <p>
          إذا كنت تعتقد أننا قد خالفنا قانون حماية البيانات الشخصية لسنة 2018، فيجوز لك
          تقديم شكوى إلى الجهة الرقابية المحدَّدة بموجب قانون حماية البيانات الشخصية لسنة
          2018 في مملكة البحرين، و/أو المطالبة بالتعويض عن الأضرار.
        </p>
        <h3>8.9 كيفية ممارسة الحقوق</h3>
        <p>
          راسلنا على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> مع
          كتابة الموضوع &quot;طلب صاحب بيانات — [اسم الحق]&quot;. وسنرد خلال 30 يوماً. وفي
          الحالات المعقَّدة، يجوز لنا التمديد لمدة 30 يوماً إضافية مع إخطارك بذلك. وقد يُطلب
          التحقُّق من هويتك قبل معالجة طلبك.
        </p>

        <h2>9. نقل البيانات عبر الحدود</h2>
        <h3>9.1</h3>
        <p>
          يتم تخزين بياناتك الشخصية ومعالجتها بشكل أساسي داخل مملكة البحرين وعلى البنية
          التحتية السحابية التي تُشغِّلها DigitalOcean (منطقة NYC3، الولايات المتحدة
          الأمريكية). وحيثما يتم نقل البيانات دولياً، تكون هناك ضمانات مناسبة قائمة.
        </p>
        <h3>9.2</h3>
        <p>
          يقوم شريكنا في تطوير التكنولوجيا FIA Global (الهند) بمعالجة البيانات الشخصية
          لأغراض تطوير المنصة وصيانتها ودعمها، وذلك بموجب اتفاقية معالجة بيانات تتضمَّن
          الشروط التعاقدية النموذجية (SCCs) التي تكفل حماية كافية معادلة لمتطلبات قانون
          حماية البيانات الشخصية البحريني لسنة 2018.
        </p>
        <h3>9.3</h3>
        <p>
          بالنسبة لخدمات توصيل الحوالات النقدية إلى المنزل، تتم مشاركة بيانات محدودة لتعريف
          المُرسل مع وكلاء التوصيل المحليين المرخَّصين في الهند ونيبال بموجب التزامات تعاقدية
          لحماية البيانات.
        </p>
        <h3>9.4</h3>
        <p>
          وحيثما ننقل بيانات شخصية خارج البحرين إلى دول لا تتوفر فيها تشريعات معادلة لحماية
          البيانات، فإننا نكفل ضمانات مناسبة تشمل: (أ) الشروط التعاقدية النموذجية (SCCs)؛
          (ب) قواعد مؤسسية مُلزمة؛ (ج) أحكاماً تعاقدية تفرض التزامات معادلة؛ أو (د) الموافقة
          الصريحة حيثما ينطبق ذلك.
        </p>
        <h3>9.5</h3>
        <p>
          يجوز لك طلب معلومات عن الضمانات المطبَّقة على عمليات النقل عبر الحدود بالتواصل معنا
          على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>.
        </p>

        <h2>10. الإخطار بخرق البيانات</h2>
        <h3>10.1</h3>
        <p>
          تحتفظ أمانات بسياسة لأمن المعلومات وبإجراءات للاستجابة للحوادث مصمَّمة لحماية
          بياناتك الشخصية.
        </p>
        <h3>10.2</h3>
        <p>
          في حالة وقوع خرق للبيانات الشخصية يُرجَّح أن يترتب عليه خطر على حقوق الأفراد
          وحرياتهم، ستقوم أمانات، وفقاً لقانون حماية البيانات الشخصية لسنة 2018، بما يلي:
        </p>
        <ul>
          <li>
            <strong>(أ)</strong> إخطار الجهة الرقابية المختصة خلال اثنتين وسبعين (72) ساعة
            من علمها بالخرق، مع تقديم: طبيعة الخرق، وفئات أصحاب البيانات المتأثرين، والعواقب
            المحتملة، والتدابير المتخذة.
          </li>
          <li>
            <strong>(ب)</strong> إخطار أصحاب البيانات المتأثرين دون تأخير لا مبرر له حيثما
            يُرجَّح أن يترتب على الخرق خطر مرتفع على حقوقهم وحرياتهم.
          </li>
          <li>
            <strong>(ج)</strong> الاحتفاظ بسجل داخلي لخروقات البيانات الشخصية يوثِّق جميع
            الخروقات وآثارها والإجراءات التصحيحية المتخذة.
          </li>
        </ul>
        <h3>10.3</h3>
        <p>
          إذا كنت تعتقد أن حسابك قد تعرَّض للاختراق، يُرجى إخطارنا فوراً على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>{" "}
          (الموضوع: &quot;حادث أمني&quot;).
        </p>

        <h2>11. سياسة ملفات تعريف الارتباط</h2>
        <h3>11.1</h3>
        <p>
          يستخدم موقعنا الإلكتروني (
          <a
            href="https://www.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.amanatfintech.com
          </a>
          ) ومتجرنا الإلكتروني (
          <a
            href="https://ecart.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecart.amanatfintech.com
          </a>
          ) ملفات تعريف الارتباط وتقنيات التتبُّع المشابهة لتحسين تجربتك، والحفاظ على جلستك،
          وتحليل الاستخدام.
        </p>
        <h3>11.2 أنواع ملفات تعريف الارتباط التي نستخدمها</h3>
        <ul>
          <li>
            <strong>(أ) ملفات تعريف الارتباط الأساسية / الضرورية للغاية:</strong> مطلوبة
            لتشغيل الموقع الإلكتروني والمتجر الإلكتروني (إدارة الجلسة، والمصادقة، والأمن).
            ولا يمكن تعطيلها دون التأثير على الأداء الوظيفي.
          </li>
          <li>
            <strong>(ب) ملفات تعريف الارتباط الخاصة بالأداء / التحليلات:</strong> تُستخدم
            لفهم تفاعلات الزوار. وهي بيانات مجمَّعة ومجهولة الهوية. وتتطلب موافقتك.
          </li>
          <li>
            <strong>(ج) ملفات تعريف الارتباط الوظيفية:</strong> تتذكَّر تفضيالتك (اللغة،
            والمنطقة، وإعدادات العرض). وتتطلب موافقتك بالنسبة لملفات تعريف الارتباط الوظيفية
            غير الأساسية.
          </li>
          <li>
            <strong>(د) ملفات تعريف الارتباط التسويقية / الاستهدافية:</strong> لا توضع إلا
            بموافقتك الصريحة. ويمكن سحب الموافقة في أي وقت.
          </li>
        </ul>
        <h3>11.3 الموافقة على ملفات تعريف الارتباط</h3>
        <p>
          عند زيارتك لموقعنا الإلكتروني أو متجرنا الإلكتروني لأول مرة، يتم عرض شريط
          للموافقة على ملفات تعريف الارتباط. ويجوز لك قبول جميع ملفات تعريف الارتباط، أو
          قبول الملفات الأساسية فقط، أو تخصيص تفضيالتك. كما يمكنك إدارة ملفات تعريف
          الارتباط من خلال إعدادات متصفحك؛ إلا أن تعطيل ملفات تعريف الارتباط الأساسية قد
          يؤثر على الأداء الوظيفي للمنصة.
        </p>
        <h3>11.4 ملفات تعريف الارتباط الخاصة بأطراف ثالثة</h3>
        <p>
          قد يضع مزوِّدو بوابات الدفع وشركاء التحليلات لدينا ملفات تعريف ارتباط خاصة بهم على
          جهازك. وتخضع هذه الملفات لسياسات الخصوصية الخاصة بتلك الأطراف الثالثة.
        </p>
        <h3>11.5 إدارة الجلسة</h3>
        <p>
          يستخدم تطبيقنا على الهاتف المحمول رموز الجلسة بدلاً من ملفات تعريف ارتباط المتصفح.
          وتنتهي صلاحية رموز الجلسة بعد 24 ساعة من عدم النشاط.
        </p>

        <h2>12. أمن المعلومات</h2>
        <h3>12.1</h3>
        <p>
          تطبِّق أمانات تدابير أمنية تقنية وتنظيمية مناسبة لحماية البيانات الشخصية، تشمل:
        </p>
        <ul>
          <li>
            <strong>(أ)</strong> تشفير البيانات أثناء النقل باستخدام TLS/SSL (بحد أدنى TLS
            1.2) وتشفير البيانات الحسَّاسة أثناء التخزين.
          </li>
          <li>
            <strong>(ب)</strong> ضوابط الوصول القائمة على الأدوار (RBAC) التي تقصر الوصول
            إلى البيانات الشخصية على الموظفين المصرَّح لهم فقط.
          </li>
          <li>
            <strong>(ج)</strong> المصادقة متعددة العوامل (MFA) للوصول الإداري إلى الأنظمة
            التي تخزِّن البيانات الشخصية.
          </li>
          <li>
            <strong>(د)</strong> تقييمات أمنية دورية، واختبارات الاختراق، وفحص الثغرات.
          </li>
          <li>
            <strong>(هـ)</strong> معالجة المدفوعات بما يتوافق مع معيار PCI-DSS — لا يتم
            تخزين بيانات البطاقات على أنظمة أمانات على الإطلاق.
          </li>
          <li>
            <strong>(و)</strong> تدريب الموظفين على أفضل ممارسات حماية البيانات وأمن
            المعلومات.
          </li>
        </ul>
        <h3>12.2</h3>
        <p>
          وعلى الرغم من بذلنا لأقصى الجهود، لا يوجد نظام آمن بشكل مطلق. ولا يمكننا ضمان
          الأمن المطلق للبيانات المنقولة عبر الإنترنت. يُرجى استخدام كلمة مرور قوية، وعدم
          مشاركة بيانات اعتماد تسجيل الدخول الخاصة بك، وتسجيل الخروج عند عدم الاستخدام.
        </p>

        <h2>13. معالجة بيانات مكافحة غسل الأموال وتمويل الإرهاب</h2>
        <h3>13.1</h3>
        <p>
          تطبِّق أمانات إجراءات اعرف عميلك (KYC)، والعناية الواجبة تجاه العملاء (CDD)،
          والعناية الواجبة المعزَّزة (EDD) وفقاً لقانون مكافحة غسل الأموال البحريني رقم
          4/2001، ووحدة مكافحة غسل الأموال وتمويل الإرهاب الصادرة عن مصرف البحرين المركزي
          (الوحدات من FC-1 إلى FC-7)، وتوصيات مجموعة العمل المالي (FATF) والتزامات التقييم
          المتبادل لمجموعة العمل المالي لمنطقة الشرق الأوسط وشمال أفريقيا (MENAFATF)
          المطبَّقة على مملكة البحرين.
        </p>
        <h3>13.2</h3>
        <p>
          لأغراض الامتثال لمتطلبات مكافحة غسل الأموال وتمويل الإرهاب، نقوم بمعالجة وتخزين
          ما يلي:
        </p>
        <ul>
          <li>وثائق التحقُّق من الهوية والبيانات البيومترية حيثما ينطبق ذلك.</li>
          <li>حالة الأشخاص المعرَّضين سياسياً (PEP) ونتائج الفحص.</li>
          <li>
            نتائج فحص العقوبات مقابل قوائم الأمم المتحدة، ومكتب مراقبة الأصول الأجنبية
            (OFAC)، وخزانة صاحبة الجلالة (HMT)، وغيرها من قوائم العقوبات المعمول بها.
          </li>
          <li>سجلات مراقبة المعاملات وتقارير المعاملات/الأنشطة المشبوهة.</li>
          <li>إقرارات مصدر الأموال والمستندات المؤيِّدة لها.</li>
        </ul>
        <h3>13.3 حظر الإفشاء (التنبيه)</h3>
        <p>
          بموجب قوانين مكافحة غسل الأموال وتمويل الإرهاب المعمول بها، يُحظر علينا قانوناً
          إخطارك (حظر &quot;الإفشاء&quot; أو &quot;التنبيه&quot;) إذا كنا قد قدَّمنا أو بصدد
          النظر في تقديم تقرير معاملات مشبوهة (STR) أو تقرير أنشطة مشبوهة (SAR) إلى إدارة
          المعلومات المالية (FID) في البحرين. ولا يسري الحق في المحو (القسم 8.3) على بيانات
          مكافحة غسل الأموال وتمويل الإرهاب حيثما يكون الاحتفاظ بها إلزامياً بموجب القانون.
        </p>

        <h2>14. خصوصية الأطفال</h2>
        <h3>14.1</h3>
        <p>
          خدماتنا مخصَّصة للأفراد البالغين من العمر 18 عاماً فأكثر، وفقاً للقانون المدني
          البحريني (المرسوم بقانون رقم 19/2001) ولوائح الخدمات المالية المعمول بها. ونحن لا
          نجمع عن علم بيانات شخصية من أفراد تقل أعمارهم عن 18 عاماً.
        </p>
        <h3>14.2</h3>
        <p>
          إذا علمنا بأنه قد تم جمع بيانات شخصية لفرد يقل عمره عن 18 عاماً دون الحصول على
          الموافقة المناسبة، فسنقوم بحذف هذه البيانات على الفور. وإذا كنت والداً أو وصياً
          وتعتقد أننا قد جمعنا بيانات طفلك، فيُرجى التواصل معنا فوراً على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>.
        </p>

        <h2>15. المراسلات التسويقية</h2>
        <h3>15.1</h3>
        <p>
          بموافقتك الصريحة المسبقة، يجوز لنا أن نرسل إليك مراسلات تسويقية بشأن منتجاتنا
          وخدماتنا ودوراتنا وعروضنا الترويجية وتحديثاتنا عبر البريد الإلكتروني والرسائل
          النصية القصيرة والإشعارات داخل التطبيق.
        </p>
        <h3>15.2</h3>
        <p>
          يجوز لك إلغاء الاشتراك في المراسلات التسويقية في أي وقت عن طريق: (أ) النقر على
          رابط &quot;إلغاء الاشتراك&quot; في أي بريد إلكتروني تسويقي؛ (ب) تعديل تفضيلات
          الإشعارات في إعدادات التطبيق؛ (ج) التواصل معنا على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a>. ولن
          يؤثر إلغاء الاشتراك على المراسلات المتعلقة بالمعاملات أو الخدمات (مثل تنبيهات
          الحساب، والإيصالات، والإشعارات الأمنية).
        </p>

        <h2>16. القانون الواجب التطبيق والاختصاص القضائي</h2>
        <h3>16.1</h3>
        <p>
          تخضع سياسة الخصوصية هذه وتُفسَّر وفقاً لقوانين مملكة البحرين، بما في ذلك قانون حماية
          البيانات الشخصية (قانون حماية البيانات الشخصية لسنة 2018، المرسوم رقم 30/2018)،
          وقانون مكافحة غسل الأموال البحريني (القانون رقم 4/2001)، وكتاب قواعد مصرف البحرين
          المركزي.
        </p>
        <h3>16.2</h3>
        <p>
          يخضع أي نزاع ينشأ عن سياسة الخصوصية هذه أو يتصل بها للاختصاص القضائي الحصري
          لمحاكم مملكة البحرين.
        </p>
        <h3>16.3</h3>
        <p>
          إذا تبيَّن أن أي حكم من أحكام سياسة الخصوصية هذه باطل أو غير قابل للتنفيذ، تظل
          الأحكام المتبقية صحيحة وقابلة للتنفيذ.
        </p>

        <h2>17. التعديلات على سياسة الخصوصية هذه</h2>
        <h3>17.1</h3>
        <p>
          تحتفظ أمانات بالحق في تحديث أو تعديل سياسة الخصوصية هذه في أي وقت لتعكس التغييرات
          في ممارساتنا أو القوانين المعمول بها أو المتطلبات الرقابية.
        </p>
        <h3>17.2</h3>
        <p>
          سنقوم بإخطارك بالتغييرات الجوهرية عن طريق: (أ) نشر سياسة خصوصية محدَّثة تحمل تاريخ
          &quot;آخر مراجعة&quot; جديداً؛ (ب) إرسال إشعار داخل التطبيق أو تنبيه بالبريد
          الإلكتروني حيثما تؤثر التغييرات بشكل جوهري على حقوقك.
        </p>
        <h3>17.3</h3>
        <p>
          يشكِّل استمرارك في استخدام خدماتنا بعد تاريخ سريان أي تحديث قبولاً منك لسياسة
          الخصوصية المعدَّلة.
        </p>

        <h2>18. بند ثنائي اللغة / BILINGUAL CLAUSE</h2>
        <h3>18.1</h3>
        <p>
          تم إعداد سياسة الخصوصية هذه باللغتين الإنجليزية والعربية. وفي حالة وجود أي تناقض
          أو تعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية وفقاً لقوانين مملكة
          البحرين.
        </p>
        <h3>18.2</h3>
        <p>
          تم إعداد سياسة الخصوصية هذه باللغتين الإنجليزية والعربية. في حالة أي تناقض أو
          تعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية وفقاً لقوانين مملكة
          البحرين.
        </p>
      </div>
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;
